import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListTripsQuery } from "store/services/tripApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { BaseTabs } from "components/Shared/BaseTabs";

const TripListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Trips" }];

  const tripListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Trip ID",
      selector: (row) => row.tripId,
      cell: (row) => (
        <div>
          <Link
            title={row?.tripId}
            to={`/app/crm/trip/${row?._id}/detail`}
            className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.tripId ? (
              <HighlightedText text={row?.tripId} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        </div>
      ),
    },
    {
      name: "Driver",
      selector: (row) => `${row.driver?.name || 'N/A'} (${row.driver?.driverId || 'N/A'})`,
      cell: (row) => (
        <>
          {row?.driver ? (
            <HighlightedText
              text={`${row.driver.name} (${row.driver.driverId})`}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </>
      ),
    },
    {
      name: "Vehicle",
      selector: (row) => row.vehicle?.vehicle_id,
      cell: (row) => (
        <>
          {row?.vehicle?.vehicle_id ? (
            <HighlightedText text={row?.vehicle?.vehicle_id} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
    },

    {
      name: "Added By",
      selector: (row) => row.addedBy?.name,
      cell: (row) => (
        <>
          {row?.addedBy?.name ? (
            <HighlightedText text={row?.addedBy?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="flex justify-center space-x-2">
          <Link to={`/app/crm/trip/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Trip" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/trip/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Trip">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  // RTK Query
  const [fetchTrips, { data: tripRes, isLoading, isFetching, error }] =
    useLazyListTripsQuery();

  useEffect(() => {
    fetchTrips({
      active: activeTab !== "Inactive",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchTrips, keyword, perPage]);

  useAppMeta(`${activeTab} Trips`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={tripListTabs}
          activeTab={activeTab}
        />

        <TableFilter
          searchLabel="Trip ID/Vehicle/Driver/Added By"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium">{activeTab} Trips</div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={tripRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={tripRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default TripListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/trip/create">
        <Button startIcon={<Add />} variant="contained">
          New Trip
        </Button>
      </Link>
    </>
  );
};